import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Truck, 
  Plus, 
  Eye, 
  Edit, 
  Phone, 
  Mail, 
  MapPin,
  Clock
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { supplierService } from '../../services/supplierService';
import { Supplier } from '../../types';

function SupplierList() {
  const { user } = useAuth();
  const { addNotification } = useNotifications();
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSuppliers();
  }, []);

  const loadSuppliers = async () => {
    try {
      setLoading(true);
      const data = await supplierService.getSuppliers();
      setSuppliers(data);
    } catch (error) {
      addNotification({
        userId: user?.id || '',
        title: 'Erreur de chargement',
        message: 'Impossible de charger les fournisseurs',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const canManageSuppliers = user?.role === 'admin' || user?.role === 'responsable_depot' || user?.role === 'responsable_bureau';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="glass-card rounded-xl card-hover">
        <div className="px-6 py-6 sm:p-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-700 rounded-lg shadow-lg">
                <Truck className="h-6 w-6 text-white" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900">Fournisseurs</h1>
            </div>
            
            {canManageSuppliers && (
              <Link
                to="/suppliers/new"
                className="btn-primary px-4 py-2 text-white text-sm rounded-lg font-medium transition-all duration-200 flex items-center space-x-2"
              >
                <Plus className="h-4 w-4" />
                <span>Nouveau fournisseur</span>
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Suppliers Grid */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {suppliers.map((supplier) => (
          <div key={supplier.id} className="glass-card rounded-xl card-hover overflow-hidden">
            <div className="px-6 py-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  {supplier.raisonSociale}
                </h3>
                <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                  supplier.isActive 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {supplier.isActive ? 'Actif' : 'Inactif'}
                </div>
              </div>
              
              <div className="space-y-3 mb-6">
                <div className="flex items-center text-sm text-gray-600">
                  <Phone className="h-4 w-4 mr-2 flex-shrink-0" />
                  <span>{supplier.contactTel || 'Non renseigné'}</span>
                </div>
                
                <div className="flex items-center text-sm text-gray-600">
                  <Mail className="h-4 w-4 mr-2 flex-shrink-0" />
                  <span className="truncate">{supplier.contactEmail || 'Non renseigné'}</span>
                </div>
                
                <div className="flex items-center text-sm text-gray-600">
                  <MapPin className="h-4 w-4 mr-2 flex-shrink-0" />
                  <span className="truncate">Adresse non renseignée</span>
                </div>
                
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">
                  Contact: {supplier.contactNom}
                </span>
                
                <div className="flex items-center space-x-2">
                  <Link
                    to={`/suppliers/${supplier.id}`}
                    className="text-blue-600 hover:text-blue-900 p-1 rounded"
                    title="Voir les détails"
                  >
                    <Eye className="h-4 w-4" />
                  </Link>
                  
                  {canManageSuppliers && (
                    <Link
                      to={`/suppliers/${supplier.id}/edit`}
                      className="text-gray-600 hover:text-gray-900 p-1 rounded"
                      title="Modifier"
                    >
                      <Edit className="h-4 w-4" />
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {suppliers.length === 0 && (
        <div className="glass-card rounded-xl">
          <div className="text-center py-12">
            <Truck className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun fournisseur</h3>
            <p className="mt-1 text-sm text-gray-500">
              Commencez par ajouter un fournisseur
            </p>
            {canManageSuppliers && (
              <div className="mt-6">
                <Link
                  to="/suppliers/new"
                  className="btn-primary px-4 py-2 text-white text-sm rounded-lg font-medium transition-all duration-200"
                >
                  Ajouter un fournisseur
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default SupplierList;